import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { WeightSlider } from "./WeightSlider"
import { DynamicInputList } from "./DynamicInputList"
import { ProgressIndicator } from "./ProgressIndicator"
import { SummarySection } from "./SummarySection"
import { FormSection } from "./FormSection"
//import { MultiSelect } from "./MultiSelect"
import { useForm, Controller, useWatch } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { DatePickerWithRange } from "@/components/ui/DatePickerWithRange"
import { DateRange } from "react-day-picker"
import { HiringCampaign } from "openapi/api/model"
import { levels } from "@/pages/HiringCampaign/utils/ConfigureAssement"
import { LocationAndWorkAuth } from "./LocationAndWorkAuthorization"
import { toast } from "sonner"
import { AnalysisResult, RequirementAnalysis } from "./RequirementAnalysis"
import { TechFilterSelect } from "./TechFilter"
import { SKILLS } from "../utils/skills-list"

const EMPLOYMENT_TYPES = ["Full-time", "Part-time", "Contract", "Internship"] as const;


const JobLocationType = [
  "Fully Remote",
  "Hybrid",
  "Office-Based"
] as const

const FORM_STEPS = [
  "Basic Info",
  //"Role configuration",
  "Project Description",
  "Skills",
  "Qualifications",
  //"Red Flags",
  "Summary",
]
const WorkAuthorizationSchema = z.object({
  // Basic requirements
  requiresSponsorship: z.boolean(),
  
  // Minimum status requirements (any of these are acceptable)
  acceptedStatuses: z.union([
    z.literal("Any legal work authorization"),
    z.object({
      US: z.array(z.enum([
        "US Citizen",
        "Green Card",
        "EAD",
        "Valid Visa with Work Rights"
      ])),
      Canada: z.array(z.enum([
        "Canadian Citizen",
        "Permanent Resident",
        "Valid Work Permit"
      ])),
      Other: z.string().optional()
    }).refine(
      (data) => data.US || data.Canada || data.Other,
      {
        message: "At least one of 'US', 'Canada', or 'Other' must be provided.",
      }
    ),
  ]),
  
  // Special requirements
  requiresSecurityClearance: z.boolean().default(false),
  requiresCitizenshipForClearance: z.boolean().optional()
})

const RequirementAnalysisSchema = z.object({
  category: z.string(),
  roles: z.array(
    z.object({
      role: z.string(),
      description: z.string(),
      weight: z.number(),
    })
  ),
  why: z.string(),
});





export const NewHiringCampaignConfigSchema = z.object({
  jobTitle: z.string().min(1, "Job title is required"),
  department: z.string().min(1, "Company name is required"),
  location: z.object({
    city: z.string(),
    state: z.string(),
    country: z.string(),
  }),
  jobLocationType:z.enum(JobLocationType),
  employmentType: z.enum(EMPLOYMENT_TYPES),
  problemSpaces: z.array(z.string()).min(1, "Problem spaces is required").max(3),
  problemSpaceWeights: z.array(z.number()),
  projectDescription: z.string().min(1, "Project description is required"),
  mainSkills: z.array(z.string()).min(1, "Main skills is required").max(2),
  niceToHaveSkills: z.array(z.string()).max(5),
  niceToHaveSkillWeights: z.array(z.number()),
  mustHaveQualifications: z.array(z.string()).min(1, "Must have qualifications is required"),
  niceToHaveQualifications: z.array(z.string()),
  niceToHaveQualificationWeights: z.array(z.number()),
  //redFlags: z.array(z.string()),
  startDate: z.string().datetime({ message: "Start date must be a valid ISO 8601 datetime." }),
  endDate: z.string().datetime({ message: "End date must be a valid ISO 8601 datetime." }),
  experienceLevel:  z.enum(levels),
  workAuthorization: WorkAuthorizationSchema,
  requirementAnalysis: RequirementAnalysisSchema

})

type FormValues = z.infer<typeof NewHiringCampaignConfigSchema>


type JobRequirementFormProps={
  initialValues?:FormValues
  disableSubmit?: boolean,
  onSubmitCallBack?:(_:HiringCampaign)=>void
}

export default function JobRequirementForm(props?:JobRequirementFormProps) { // add submit and optional default values on the form
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    setError,
    trigger,
    getValues,
    clearErrors,
    handleSubmit
  } = useForm<FormValues>({
    resolver: zodResolver(NewHiringCampaignConfigSchema),
    defaultValues: {
      jobTitle: props?.initialValues?.jobTitle??"",
      department: props?.initialValues?.department??"",
      location: {
        city:"",
        country:"",
        state:""
      },
      jobLocationType:"Fully Remote",
      employmentType: props?.initialValues?.employmentType??undefined,
      experienceLevel: props?.initialValues?.experienceLevel??undefined,
      problemSpaces: props?.initialValues?.problemSpaces??[],
      problemSpaceWeights: props?.initialValues?.problemSpaceWeights??[],
      projectDescription: props?.initialValues?.projectDescription??"",
      mainSkills: props?.initialValues?.mainSkills??[],
      niceToHaveSkills: props?.initialValues?.niceToHaveSkills??[],
      niceToHaveSkillWeights: props?.initialValues?.niceToHaveSkillWeights??[],
      mustHaveQualifications: props?.initialValues?.mustHaveQualifications??[],
      niceToHaveQualifications:  props?.initialValues?.niceToHaveQualifications??[],
      niceToHaveQualificationWeights:  props?.initialValues?.niceToHaveQualificationWeights??[],
      //redFlags: props?.initialValues?.redFlags??[],
      startDate:props?.initialValues?.startDate??undefined,
      endDate:props?.initialValues?.endDate??undefined,
      workAuthorization:{
        acceptedStatuses:'',
        requiresCitizenshipForClearance:false,
        requiresSecurityClearance:false,
        requiresSponsorship:false
      },
      requirementAnalysis: {
        category: "",
        roles: [],
        why: "",
      },

    },
    mode: "onChange",
    reValidateMode: "onChange",
  })
  const requirementAnalysis = useWatch({ control, name: "requirementAnalysis" })

  const [activeSection, setActiveSection] = useState(0)

  useEffect(() => {
    trigger()
  }, [activeSection])

  const formatFormData = (data: FormValues):HiringCampaign => {
    const problemSpaces=data.problemSpaces.map((category, index) => ({
      category,
      weight: data.problemSpaceWeights[index],
    }))
    if(problemSpaces.length==0){
      problemSpaces.push({category:"Data-Intensive Applications",weight:1})
    }
    return {
      experienceLevel:data.experienceLevel, 
      position: data.jobTitle,
      companyName: data.department,
      location: `${data.location.city}, ${data.location.state}, ${data.location.country}`,
      employmentType: data.employmentType,
      problemSpaces,//forcing a problem space
      projectDescription: data.projectDescription,
      mainSkills: data.mainSkills.map(mainSkill => SKILLS.find(skillObj => skillObj.label === mainSkill)?.id??''),
      niceToHaveSkills: data.niceToHaveSkills.map((niceToHaveSkill, index) => ({
        skill: SKILLS.find(skillObj => skillObj.label === niceToHaveSkill)?.id??'',
        weight: data.niceToHaveSkillWeights[index],
      })),
      qualifications: {
        mustHave: data.mustHaveQualifications,
        niceToHave: data.niceToHaveQualifications.map((qualification, index) => ({
          qualification,
          weight: data.niceToHaveQualificationWeights[index],
        })),
      },
      redFlags: [],
      startDate:data.startDate,
      endDate:data.endDate,
      workAuthorization:data.workAuthorization,
      locationFlexibility:data.jobLocationType,
      role:{
        classification: {
          category:data.requirementAnalysis.category,
          roles:data.requirementAnalysis.roles,
          why:data.requirementAnalysis.why
      }},
      licenseId:"",
      manuallyClosed:false

    } 
  }

  const onSubmit = () => {
    try{
      //setDisableSubmit(true)
      const data=getValues()
      const formattedData = formatFormData(data)
      console.log("Form submitted:", formattedData)
      for (let i = 0; i < FORM_STEPS.length; i++) {
        const completeness=isSectionCompleted(i)
        if (!completeness){
          throw new Error(`Section ${i+1} is incomplete`)
        }
      }
      props?.onSubmitCallBack&&props?.onSubmitCallBack(formattedData)
    }catch(err){
      toast.error((err as Error).message,{
        description:"Please make sure to verify the form before submitting",
        action: {
            label: "Dismiss",
            onClick: () => {},
        },
        style: {
            background: '#dc2626'
        }
    })
    }finally{
      //setDisableSubmit(false)
    }
  }

  const isSectionCompleted = (section: number) => {
    const formValues = getValues()
    switch (section) {
      case 0:
        return !!(formValues.jobTitle && formValues.department && formValues.location.city && formValues.employmentType && formValues.startDate && formValues.endDate &&formValues.experienceLevel)
      case 1:
        return !!(formValues.requirementAnalysis.category && formValues.requirementAnalysis.roles && formValues.requirementAnalysis.why)
      //case 2:
      //  return formValues.projectDescription.length > 0
      case 2:
        return formValues.mainSkills.length > 0
      case 3:
        return formValues.mustHaveQualifications.length > 0
      // case 5:
      //   return formValues.redFlags.length > 0
      case 4:
        return formValues.mustHaveQualifications.length > 0 // Summary section is always completed
      default:
        return false
    }
  }

  const handleStepClick = async (step: number) => {
    if (
      step <=
      Math.max(...FORM_STEPS.map((_, index) => (isSectionCompleted(index) ? index : -1)).filter((i) => i !== -1)) + 1
    ) {
      await trigger()
      setActiveSection(step)
    }
  }

  const handleAnalysisComplete = ({classification,projectDescription}: AnalysisResult) => {
    console.log({classification})
    setValue("requirementAnalysis", classification, { shouldValidate: true })
    setValue("projectDescription",projectDescription, { shouldValidate: true })
  }

  const handleDateRangeSelect=(range?: DateRange)=>{
    if(!range)return
    setValue("startDate",(range.from as Date)?.toISOString()??undefined)
    setValue("endDate",(range.to as Date)?.toISOString()??undefined)
    const formValues = getValues()
    if(!formValues.startDate){
      setError("startDate",{
        type:'manual',
        message:"Missing start date"})
      return
    }
    clearErrors("startDate")


    if(!formValues.endDate){
      setError("endDate",{
        type:"manual",
        message:"Missing end date",
      })
      return
    }
    clearErrors("endDate")
    
    

  }

  return (
    <form onSubmit={handleSubmit(()=>{})} className="max-w-3xl mx-auto space-y-6 py-8">
      <div className="space-y-2 text-left">
        <h1 className="text-2xl font-bold">Job Requirement Questionnaire</h1>
        <p className="text-muted-foreground">Fill out the form below to create a detailed job requirement profile</p>
      </div>

      <ProgressIndicator
        steps={FORM_STEPS}
        currentStep={activeSection}
        completedSteps={FORM_STEPS.map((_, index) => (isSectionCompleted(index) ? index : -1)).filter((i) => i !== -1)}
        onStepClick={handleStepClick}
      />

      {activeSection === 0 && (
        <FormSection title="1. Basic Information" className="bg-white" isActive={true} isCompleted={isSectionCompleted(0)}>
          <div className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="jobTitle">Job Title</Label>
              <Controller
                name="jobTitle"
                control={control}
                render={({ field }) => <Input id="jobTitle" placeholder="e.g., Senior Backend Engineer" {...field} />}
              />
              {errors.jobTitle && <p className="text-sm text-red-500">{errors.jobTitle?.message}</p>}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="department">Company Name</Label>
              <Controller
                name="department"
                control={control}
                render={({ field }) => <Input id="department" placeholder="e.g., Acme Inc." {...field} />}
              />
              {errors.department && <p className="text-sm text-red-500">{errors.department?.message}</p>}
            </div>


            <div className="grid gap-2">
              <Label htmlFor="employmentType">Employment Type</Label>
              <Controller
                name="employmentType"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select employment type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {EMPLOYMENT_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.employmentType && <p className="text-sm text-red-500">{errors.employmentType?.message}</p>}
            </div>
            <div className="grid gap-2">
              <Label>Running period</Label>
              <DatePickerWithRange 
                onSubmitProp={handleDateRangeSelect} 
                currentValue={{
                  from:new Date(getValues("startDate")??new Date()),
                  to:new Date(getValues("endDate")??new Date())
                  }} />
              {(errors.startDate || errors.endDate) && (
                <p className="text-sm text-red-500">
                  {errors.startDate?.message || errors.endDate?.message}
                </p>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="experienceLevel">Experience level (years)</Label>
              <Controller
                name="experienceLevel"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select employment type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {levels.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.experienceLevel && <p className="text-sm text-red-500">{errors.experienceLevel?.message}</p>}
            </div>
            <div>
              <Controller
                name="location"
                control={control}
                render={({ field }) => (
                  <LocationAndWorkAuth
                    formData={{
                      location: field.value,
                      workAuthorization: watch("workAuthorization"),
                    }}
                    onLocationChange={(location) => {
                      field.onChange(location)
                      setValue("workAuthorization", {
                        ...watch("workAuthorization"),
                        acceptedStatuses: "Any legal work authorization",
                      })
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    onWorkAuthChange={(field, value) => setValue(`workAuthorization.${field}` as any, value)}
                  />
                )}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="">Location Flexibility:</Label>
              <Controller
                name="jobLocationType"
                control={control}
                render={({ field }) => (
                  <Select onValueChange={field.onChange} value={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select job location type" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {JobLocationType.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.jobLocationType && <p className="text-sm text-red-500">{errors.jobLocationType?.message}</p>}
            </div>

          </div>
        </FormSection>
      )}

      {activeSection === 1 && (
        <FormSection title="2. Requirement Analysis" className="bg-white" isActive={true} isCompleted={isSectionCompleted(1)}>
          <RequirementAnalysis onAnalysisComplete={handleAnalysisComplete} initialValue={watch("projectDescription")} />
          {requirementAnalysis.category && (
            <div className="mt-4 space-y-4">
              <div>
                <Label>Category</Label>
                <p className="text-sm font-medium">{requirementAnalysis.category}</p>
              </div>
              <div>
                <Label>Roles</Label>
                <ul className="list-disc list-inside space-y-2">
                  {requirementAnalysis.roles.map((role, index) => (
                    <li key={index} className="text-sm">
                      <span className="font-medium">{role.role}</span> ({(role.weight * 100).toFixed(1)}%)
                      <p className="ml-4 text-muted-foreground">{role.description}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <Label>Analysis</Label>
                <p className="text-sm">{requirementAnalysis.why}</p>
              </div>
            </div>
          )}
        </FormSection>
      )}
      {
        /*
        
              {activeSection === 2 && (
        <FormSection title="3. Project Description" isActive={true}  className="bg-white" isCompleted={isSectionCompleted(2)}>
          <div className="space-y-4">
            <Label htmlFor="projectDescription">Describe the project the candidate will work on</Label>
            <Controller
              name="projectDescription"
              control={control}
              render={({ field }) => (
                <Textarea
                  id="projectDescription"
                  placeholder="e.g., Build and scale a real-time analytics platform..."
                  {...field}
                />
              )}
            />
            {errors.projectDescription && <p className="text-sm text-red-500">{errors.projectDescription?.message}</p>}
          </div>
        </FormSection>
      )}
        */

      }




      {activeSection === 2 && (
        <FormSection 
          title="4. Skills" 
          isActive={true} 
          className=" bg-white"
          description={`
            Main skill is a skill that is a must-have; resumes lacking this skill will be marked as unqualified. 
            Nice-to-have skills can be priorized by order of relative importance to
            detect and rank candidates with truly exceptional fit.
            `} 
          isCompleted={isSectionCompleted(3)}>
          <div className="space-y-4">
            <div>
              <Label>Main Skills (Select 1)</Label>
              <Controller
                name="mainSkills"
                control={control}
                render={({ field }) => (
                  <TechFilterSelect
                    value={field.value}
                    onChange={field.onChange}
                    //onBlur={field.onBlur}
                    multiSelect={false}
                    placeholder="Select main skill"
                    //error={fieldState.error}
                  /> 
                )}
              />
              {errors.mainSkills && <p className="text-sm text-red-500">{errors.mainSkills?.message}</p>}
            </div>

            <div>
              <Label>Nice-to-Have Skills (Select up to 5)</Label>
              <Controller
                name="niceToHaveSkills"
                control={control}
                render={({ field }) => (
                  <TechFilterSelect
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value)
                      setValue("niceToHaveSkillWeights", new Array(value.length).fill(1 / value.length))
                      trigger("niceToHaveSkills")
                      trigger("niceToHaveSkillWeights")
                    }}
                    multiSelect={true}
                    placeholder="Select nice-to-have skills"
                  />
                )}
              />
              {errors.niceToHaveSkills && <p className="text-sm text-red-500">{errors.niceToHaveSkills?.message}</p>}
            </div>

            <Controller
              name="niceToHaveSkillWeights"
              control={control}
              render={({ field }) => (
                <WeightSlider items={watch("niceToHaveSkills")} weights={field.value} onChange={field.onChange} />
              )}
            />
          </div>
        </FormSection>
      )}

      {activeSection === 3 && (
        <FormSection title="5. Qualifications" isActive={true} className=" bg-white" isCompleted={isSectionCompleted(4)}>
          <div className="space-y-4">
            <div>
              <Label>Must-Have Qualifications</Label>
              <Controller
                name="mustHaveQualifications"
                control={control}
                render={({ field }) => (
                  <DynamicInputList
                    items={field.value}
                    onChange={field.onChange}
                    placeholder="Enter a must-have qualification"
                  />
                )}
              />
            </div>
            {errors.mustHaveQualifications && <p className="text-red-500">{errors.mustHaveQualifications?.message}</p>}

            <div>
              <Label>Nice-to-Have Qualifications</Label>
              <Controller
                name="niceToHaveQualifications"
                control={control}
                render={({ field }) => (
                  <DynamicInputList
                    items={field.value}
                    onChange={(value) => {
                      field.onChange(value)
                      setValue("niceToHaveQualificationWeights", new Array(value.length).fill(1 / value.length))
                      trigger("niceToHaveQualifications")
                      trigger("niceToHaveQualificationWeights")
                    }}
                    placeholder="Enter a nice-to-have qualification"
                  />
                )}
              />
            </div>
            <Controller
              name="niceToHaveQualificationWeights"
              control={control}
              render={({ field }) => (
                <WeightSlider
                  items={watch("niceToHaveQualifications")}
                  weights={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            {errors.niceToHaveQualifications && (
              <p className="text-red-500">{errors.niceToHaveQualifications?.message}</p>
            )}
          </div>
        </FormSection>
      )}

      {/* {activeSection === 5 && (
        <FormSection title="6. Red Flags" isActive={true} className=" bg-white" isCompleted={isSectionCompleted(5)}>
          <div className="space-y-4">
            <Label>Potential Red Flags</Label>
            <Controller
              name="redFlags"
              control={control}
              render={({ field }) => (
                <DynamicInputList
                  items={field.value}
                  onChange={field.onChange}
                  placeholder="Enter a potential red flag"
                />
              )}
            />
          </div>
        </FormSection>
      )} */}

      {activeSection === 4 && (
        <FormSection title="6. Summary" isActive={true} className=" bg-white" isCompleted={true}>
          <SummarySection data={formatFormData(watch())} />
        </FormSection>
      )}

      <div className="flex justify-between mt-6">
        <Button
          type="button"
          variant="outline"
          onClick={() => setActiveSection((prev) => Math.max(0, prev - 1))}
          disabled={activeSection === 0}
        >
          Previous
        </Button>
        {activeSection < FORM_STEPS.length - 1 ? (
          <Button
            type="button"
            className="bg-blue100 hover:bg-primary/90 text-white"
            onClick={() => setActiveSection((prev) => Math.min(FORM_STEPS.length - 1, prev + 1))}
            disabled={
              !FORM_STEPS.map((_, index) => (isSectionCompleted(index) ? index : -1))
              .filter((i) => i !== -1).
              includes(activeSection) || 
              (watch("workAuthorization.acceptedStatuses") !== "Any legal work authorization" &&
              Object.keys(watch("workAuthorization.acceptedStatuses.Canada") ?? {}).length === 0 &&
              Object.keys(watch("workAuthorization.acceptedStatuses.US") ?? {}).length === 0)}
          >
            Next
          </Button>
        ) : (
          <Button
            className="bg-teal100 hover:bg-primary/90 text-white"
            onClick={onSubmit}
            disabled={props?.disableSubmit}
          >
            Confirm Requirements
          </Button>
        )}
      </div>
    </form>
  )
}